import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Field} from 'entities/chartFilters';
import {ChartFilterType} from '@/views/storyboard/utils/chartConstants';
import useData from '@/utils/useData';
import {buildURL} from '@/utils/urlBuilder';
import Icon from '@/components/icons/Icon';
import i18n from 'i18n';

interface FormField {
    value: string;
    label?: string;
    type?: ChartFilterType;
}

interface Props {
    formField: FormField;
    setFieldValue: (fieldName: string, fieldValue: Field[]) => void;
    value?: Field[];
    parent: string;
}

type EventFilterChoices<T extends string = string> = [T, T];

const ChartSettingsMultiFilterSelect = React.memo<Props>(({
    formField,
    setFieldValue,
    value = [],
    parent,
}) => {
    const [open, setOpen] = useState(false);
    const [searchString, setSearchString] = useState('');
    const triggerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const cleanFieldName = formField.value.replace('all_fields.', '').replace('allFields.', '');
    const requestParams = useMemo(() => ({
        params: {
            q: searchString,
        },
    }), [searchString]);

    const [filterChoices, loading] = useData<EventFilterChoices>(
        buildURL('eventFilterChoicesResource', {filter_name: cleanFieldName}),
        {
            useLocalStore: true,
            initialValue: [],
            requestParams,
        },
    );

    const options = useMemo(() => filterChoices.map((el) => ({
        value: el[0],
        label: el[1],
    })), [filterChoices]);

    const selectedValues = useMemo(() => new Set(value.map((el) => el.value)), [value]);
    const allVisibleSelected = options.length > 0 && options.every((option) => selectedValues.has(option.value));

    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                triggerRef.current
                && !triggerRef.current.contains(target)
                && dropdownRef.current
                && !dropdownRef.current.contains(target)
            ) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const removeValue = useCallback((removedValue: string) => {
        setFieldValue(formField.value, value.filter((item) => item.value !== removedValue));
    }, [formField.value, setFieldValue, value]);

    const toggleValue = useCallback((option: Field) => {
        if (selectedValues.has(option.value)) {
            removeValue(option.value);
            return;
        }

        setFieldValue(formField.value, [...value, option]);
    }, [formField.value, removeValue, selectedValues, setFieldValue, value]);

    const clearAll = useCallback(() => {
        setFieldValue(formField.value, []);
    }, [formField.value, setFieldValue]);

    const toggleAllVisible = useCallback(() => {
        if (allVisibleSelected) {
            const visibleValues = new Set(options.map((option) => option.value));
            setFieldValue(formField.value, value.filter((item) => !visibleValues.has(item.value)));
            return;
        }

        const existing = new Set(value.map((item) => item.value));
        const nextValues = [
            ...value,
            ...options.filter((option) => !existing.has(option.value)),
        ];

        setFieldValue(formField.value, nextValues);
    }, [allVisibleSelected, formField.value, options, setFieldValue, value]);

    return (
        <>
            <div className="chart-settings-multi-filter" ref={triggerRef}>
                <div className="chart-settings-multi-filter__chips">
                    {value.map((item) => (
                        <span className="chart-settings-multi-filter__chip" key={item.value}>
                            <span className="chart-settings-multi-filter__chip-label">
                                {item.label || item.value}
                            </span>
                            <button
                                type="button"
                                className="chart-settings-multi-filter__chip-remove"
                                onClick={() => removeValue(item.value)}
                                aria-label={i18n.t('ft_remove_filter.text')}
                            >
                                <Icon name="mdi-close" size="11px" />
                            </button>
                        </span>
                    ))}
                    <button
                        type="button"
                        className="chart-settings-multi-filter__add"
                        onClick={() => setOpen((prev) => !prev)}
                    >
                        {i18n.t('chart_settings_filter_add.text')}
                    </button>
                </div>
            </div>

            {open && (
                <div className="chart-settings-multi-filter__dropdown" ref={dropdownRef}>
                    <div className="chart-settings-multi-filter__search-wrap">
                        <input
                            id={`${parent}-smart-multi-input-${cleanFieldName}`}
                            className="chart-settings-multi-filter__search"
                            value={searchString}
                            onChange={(e) => setSearchString(e.target.value)}
                            placeholder={i18n.t('chart_settings_filter_search_values.text')}
                            autoFocus
                        />
                        {searchString ? (
                            <button
                                type="button"
                                className="chart-settings-multi-filter__search-clear"
                                onClick={() => setSearchString('')}
                                aria-label={i18n.t('chart_settings_filter_clear_search.text')}
                            >
                                <Icon name="mdi-close" size="14px" />
                            </button>
                        ) : (
                            <Icon name="mdi-magnify" size="14px" className="chart-settings-multi-filter__search-icon" />
                        )}
                    </div>

                    <div className="chart-settings-multi-filter__section-head">
                        <span>{i18n.t('chart_settings_filter_selected_count.text', {count: value.length})}</span>
                        {value.length > 0 && (
                            <button type="button" onClick={clearAll}>
                                {i18n.t('chart_settings_filter_clear_all.text')}
                            </button>
                        )}
                    </div>

                    <div className="chart-settings-multi-filter__chips chart-settings-multi-filter__chips--selected">
                        {value.map((item) => (
                            <span className="chart-settings-multi-filter__chip" key={`selected-${item.value}`}>
                                <span className="chart-settings-multi-filter__chip-label">
                                    {item.label || item.value}
                                </span>
                                <button
                                    type="button"
                                    className="chart-settings-multi-filter__chip-remove"
                                    onClick={() => removeValue(item.value)}
                                    aria-label={i18n.t('ft_remove_filter.text')}
                                >
                                    <Icon name="mdi-close" size="11px" />
                                </button>
                            </span>
                        ))}
                    </div>

                    <div className="chart-settings-multi-filter__section-head">
                        <span>
                            {options.length
                                ? i18n.t('chart_settings_filter_all_options_count.text', {count: options.length})
                                : i18n.t('chart_settings_filter_all_options.text')}
                        </span>
                        {options.length > 0 && (
                            <button type="button" onClick={toggleAllVisible}>
                                {allVisibleSelected ? i18n.t('chart_settings_filter_deselect_all.text') : i18n.t('jt_select_all.text')}
                            </button>
                        )}
                    </div>

                    <div className="chart-settings-multi-filter__options">
                        {loading ? (
                            <div className="chart-settings-multi-filter__empty">
                                {i18n.t('jt_loading_opt.text')}
                            </div>
                        ) : options.length === 0 ? (
                            <div className="chart-settings-multi-filter__empty">
                                <Icon name="mdi-magnify" size="22px" />
                                <strong>{i18n.t('jt_no_results_found.text')}</strong>
                                <span>{i18n.t('chart_settings_filter_try_different_search.text')}</span>
                            </div>
                        ) : (
                            options.map((option) => (
                                <label className="chart-settings-multi-filter__option" key={option.value}>
                                    <input
                                        type="checkbox"
                                        checked={selectedValues.has(option.value)}
                                        onChange={() => toggleValue(option)}
                                    />
                                    <span>{option.label}</span>
                                </label>
                            ))
                        )}
                    </div>

                </div>
            )}
        </>
    );
});

export default ChartSettingsMultiFilterSelect;
